import React from 'react'
import Table from '@/components/table/Table'
import Row from '@/components/table/Row'
import CellList from '@/components/table/CellList'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import AddField from '@/components/addField/AddField'
import PopupSelect from '@/components/popupSelect/PopupSelect'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import { useLazyGetSchemaQuery } from '@/store/apis/schemas'
import {
  useLazyGetSceneGroupsQuery,
  useLazyAddSceneGroupQuery,
  useLazyDeleteSceneGroupQuery,
  useLazyUpdateSceneGroupQuery,
  useLazyAddScenesToScenGroupQuery,
  useLazyRemoveSceneFromScenGroupQuery,
  useLazyUpdateSortOrderQuery,
  ISceneGroupCreate,
  ISceneGroup,
  ISceneGroupUpdate,
} from '@/store/apis/sceneGroups'
import { useLazyGetScenesQuery, ISceneObject } from '@/store/apis/scenes'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CSVLink } from 'react-csv'
import PdfSceneGroups from './PdfSceneGroups'

interface IState {
  selectedPopup?:
    | 'ADD'
    | 'DELETE'
    | 'ADD_SCENES'
    | 'REMOVE_SCENE'
    | 'CREATE_PDF'
  deleteSceneGroup?: ISceneGroup
  addSceneGroup?: ISceneGroupCreate
  editSceneGroup?: ISceneGroupUpdate
  addSceneGroupScenes?: ISceneObject[]
  selectedSceneGroup?: ISceneGroup
  removeScene?: ISceneObject
  csvData?: string[][]
}

const SceneGroups: React.FC = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const [getSchemaQuery, { data: schemaData, isLoading: isGettingSchema }] =
    useLazyGetSchemaQuery()
  const [
    getSceneGroupsQuery,
    { data: sceneGroupsData, isLoading: isGettingSceneGroups },
  ] = useLazyGetSceneGroupsQuery()
  const [addSceneGroupQuery, { isFetching: isAddingScene }] =
    useLazyAddSceneGroupQuery()
  const [updateSceneGroupQuery, { isFetching: isUpdatingScene }] =
    useLazyUpdateSceneGroupQuery()
  const [deleteSceneGroupQuery, { isFetching: isDeletingScene }] =
    useLazyDeleteSceneGroupQuery()
  const [addScenesToScenGroup, { isFetching: isAddingScenes }] =
    useLazyAddScenesToScenGroupQuery()
  const [removeSceneFromScenGroup, { isFetching: isRemovingScene }] =
    useLazyRemoveSceneFromScenGroupQuery()
  const [getScenesQuery] = useLazyGetScenesQuery()
  const [updateSortOrder] = useLazyUpdateSortOrderQuery()
  const [state, setState] = React.useState<IState>({})
  const [sceneGroups, setSceneGroups] = React.useState<ISceneGroup[]>()
  const schemaId = urlParams?.schemaId ? Number(urlParams.schemaId) : -1

  React.useEffect(() => {
    if (urlParams?.schemaId) {
      getSchemaQuery({ id: schemaId })
      getSceneGroupsQuery({ schemaId })
    }
    getScenesQuery({ schemaId })
  }, [getSchemaQuery, getSceneGroupsQuery, urlParams, getScenesQuery])

  React.useEffect(() => {
    if (sceneGroupsData && schemaData) {
      let csvData = [[t('name'), t('scenes'), t('users')]]
      csvData = csvData.concat(
        sceneGroupsData.items.map((sceneGroup) => {
          const alreadyAddedUsers: number[] = []
          sceneGroup.scenes.map((scene) => {
            const foundScene = schemaData.scenes.find(
              (schemaScene) => schemaScene.scene.id === scene.id,
            )
            return foundScene.users.map((user) => {
              if (!alreadyAddedUsers.includes(user.id)) {
                alreadyAddedUsers.push(user.id)
              }
            })
          })
          const userData = schemaData.users.filter((user) =>
            alreadyAddedUsers.includes(user.id),
          )
          const users =
            userData.length < schemaData.users.length
              ? userData
                  .map((user) => `${user.firstName} ${user.lastName}`)
                  .join(', ')
              : t('pdf.all').toUpperCase()
          return [sceneGroup.sceneGroup.name, users]
        }),
      )

      setState({
        ...state,
        csvData,
      })
      setSceneGroups(sceneGroupsData.items)
    }
  }, [sceneGroupsData, schemaData])

  const handleAdd = async (): Promise<void> => {
    if (state.addSceneGroup) {
      await addSceneGroupQuery({ schemaId, ...state.addSceneGroup })
      if (schemaId) {
        getSceneGroupsQuery({ schemaId })
      }
      setState({
        ...state,
        addSceneGroup: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDelete = async (id: number): Promise<void> => {
    await deleteSceneGroupQuery({ schemaId, id })
    if (schemaId) {
      getSceneGroupsQuery({ schemaId })
    }
    setState({
      ...state,
      selectedPopup: undefined,
      deleteSceneGroup: undefined,
    })
  }

  const handleEdit = async (): Promise<void> => {
    if (state.editSceneGroup) {
      await updateSceneGroupQuery({ schemaId, ...state.editSceneGroup })
      if (urlParams?.schemaId) {
        getSceneGroupsQuery({ schemaId })
      }
      setState({ ...state, editSceneGroup: undefined })
    }
  }

  const handleAddScenes = async (ids: number[]) => {
    if (state.selectedSceneGroup) {
      const filteredIds = ids.filter(
        (id) =>
          state.selectedSceneGroup.scenes.findIndex(
            (scene) => scene.id === id,
          ) === -1,
      )
      if (filteredIds.length) {
        await addScenesToScenGroup({
          schemaId,
          id: state.selectedSceneGroup.sceneGroup.id,
          sceneIds: filteredIds,
        })
        if (urlParams?.schemaId) {
          getSceneGroupsQuery({ schemaId })
        }
        setState({
          ...state,
          selectedSceneGroup: undefined,
          selectedPopup: undefined,
        })
      }
    }
  }

  const handleSceneRemove = async (id: number): Promise<void> => {
    if (state.selectedSceneGroup) {
      await removeSceneFromScenGroup({
        id: state.selectedSceneGroup.sceneGroup.id,
        userId: id,
      })
      if (urlParams?.schemaId) {
        getSceneGroupsQuery({ schemaId })
      }
      setState({
        ...state,
        selectedSceneGroup: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDragEnd = async (dropResult: DropResult) => {
    if (dropResult.destination && sceneGroups && schemaData) {
      const tempData = [...sceneGroups]
      const item = tempData.splice(dropResult.source.index, 1)[0]
      tempData.splice(dropResult.destination.index, 0, item)
      const sortIds = tempData.map((sceneGroup, index) => ({
        id: sceneGroup.sceneGroup.id,
        sortId: index,
      }))
      setSceneGroups(tempData)
      await updateSortOrder({ schemaId, sortIds })
      await getSceneGroupsQuery({ schemaId })
    }
  }

  const {
    selectedPopup,
    deleteSceneGroup,
    addSceneGroup,
    removeScene,
    editSceneGroup,
    selectedSceneGroup,
    addSceneGroupScenes,
    csvData,
  } = state

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tableBodyDropArea">
          {(dropProvider) => (
            <Table
              dropRef={dropProvider.innerRef}
              dropProps={dropProvider.droppableProps}
              headerLabels={[
                t('headers.name'),
                t('headers.numberOfScenes'),
                t('headers.numberOfTimeslots'),
              ]}
              headerActions={[
                {
                  icon: <PictureAsPdfIcon />,
                  disabled: isGettingSchema || isGettingSceneGroups,
                  onClick: () => {
                    setState({
                      ...state,
                      selectedPopup: 'CREATE_PDF',
                    })
                  },
                },
                {
                  icon: (
                    <CSVLink
                      data={csvData ?? []}
                      style={{ color: 'inherit' }}
                      filename={`${t('sceneGroupsPdf.title')} - ${schemaData?.schema.schema.name}.csv`}
                    >
                      <DescriptionIcon />
                    </CSVLink>
                  ),
                  disabled: isGettingSchema || isGettingSceneGroups,
                },
              ]}
              showExpand={true}
              isLoading={isGettingSchema || isGettingSceneGroups}
              isEditing={!!editSceneGroup}
              isAddOpen={selectedPopup === 'ADD'}
              onAddOpen={() =>
                schemaData?.schema &&
                setState({
                  ...state,
                  selectedPopup: 'ADD',
                  addSceneGroup: {
                    name: '',
                  },
                })
              }
            >
              {schemaData?.schema &&
                sceneGroups?.map((sceneGroup, index) => (
                  <Draggable
                    key={sceneGroup.sceneGroup.name}
                    draggableId={sceneGroup.sceneGroup.name}
                    index={index}
                  >
                    {(dragProvider) => (
                      <Row
                        dragProps={dragProvider.draggableProps}
                        dragHandleProps={dragProvider.dragHandleProps}
                        dragRef={dragProvider.innerRef}
                        key={index}
                        showExpand={true}
                        isLoading={isUpdatingScene}
                        disableButtons={!!editSceneGroup}
                        onEditOpen={() =>
                          setState({
                            ...state,
                            editSceneGroup: sceneGroup.sceneGroup,
                          })
                        }
                        onEditSuccess={() => handleEdit()}
                        onEditAbort={() =>
                          setState({ ...state, editSceneGroup: undefined })
                        }
                        onDeleteOpen={() =>
                          setState({
                            ...state,
                            selectedPopup: 'DELETE',
                            deleteSceneGroup: sceneGroup,
                          })
                        }
                        columns={{
                          normal: [
                            sceneGroup.sceneGroup.name,
                            <CellList
                              value={sceneGroup.scenes.length}
                              list={sceneGroup.scenes.map(
                                (scene) => scene.name,
                              )}
                            />,
                            sceneGroup.sceneGroup.timeslotCount,
                          ],
                          edit: editSceneGroup && [
                            <AddField
                              value={editSceneGroup.name}
                              required={true}
                              regex={/^$/}
                              onChange={(newValue) =>
                                setState({
                                  ...state,
                                  editSceneGroup: {
                                    ...editSceneGroup,
                                    name: newValue,
                                  },
                                })
                              }
                            />,
                            <CellList
                              value={sceneGroup.scenes.length}
                              list={sceneGroup.scenes.map(
                                (scene) => scene.name,
                              )}
                            />,
                            sceneGroup.sceneGroup.timeslotCount,
                          ],
                        }}
                      >
                        <Table
                          isLoading={false}
                          headerLabels={[t('headers.name')]}
                          onAddOpen={() => {
                            setState({
                              ...state,
                              selectedPopup: 'ADD_SCENES',
                              addSceneGroupScenes: sceneGroup.scenes,
                              selectedSceneGroup: sceneGroup,
                            })
                          }}
                        >
                          {[...sceneGroup.scenes]
                            .sort((a, b) => (a.name < b.name ? -1 : 1))
                            .map((scene, index) => (
                              <Row
                                key={index}
                                onDeleteOpen={() =>
                                  setState({
                                    ...state,
                                    selectedPopup: 'REMOVE_SCENE',
                                    removeScene: scene,
                                    selectedSceneGroup: sceneGroup,
                                  })
                                }
                                columns={{
                                  normal: [scene.name],
                                }}
                              />
                            ))}
                        </Table>
                      </Row>
                    )}
                  </Draggable>
                ))}
              {dropProvider.placeholder}
              {selectedPopup === 'ADD' && addSceneGroup && (
                <Row
                  onAddSuccess={() => handleAdd()}
                  onAddAbort={() =>
                    setState({
                      ...state,
                      addSceneGroup: undefined,
                      selectedPopup: undefined,
                    })
                  }
                  isLoading={isAddingScene}
                  columns={{
                    normal: [
                      '',
                      '',
                      <AddField
                        value={addSceneGroup.name}
                        required={true}
                        regex={/^$/}
                        onChange={(newValue) =>
                          setState({
                            ...state,
                            addSceneGroup: { ...addSceneGroup, name: newValue },
                          })
                        }
                      />,
                      0,
                      0,
                    ],
                  }}
                />
              )}
            </Table>
          )}
        </Droppable>
      </DragDropContext>
      <PopupDialog
        isOpen={selectedPopup === 'DELETE'}
        title={
          <>
            {t('popups.removeSceneGroupTitle')}:{' '}
            <b>{deleteSceneGroup?.sceneGroup.name}</b>
          </>
        }
        content={t('popups.removeSceneGroupDescription')}
        isLoading={isDeletingScene}
        onClose={() => setState({ ...state, selectedPopup: undefined })}
        onConfirm={() =>
          deleteSceneGroup?.sceneGroup.id
            ? handleDelete(deleteSceneGroup.sceneGroup.id)
            : null
        }
      />
      <PopupDialog
        isOpen={selectedPopup === 'REMOVE_SCENE'}
        title={
          <>
            {t('popups.removeSceneFromSceneTitle')}: <b>{removeScene?.name}</b>
          </>
        }
        content={t('popups.removeSceneFromSceneDescription')}
        isLoading={isRemovingScene}
        onClose={() => setState({ ...state, selectedPopup: undefined })}
        onConfirm={() =>
          removeScene?.id ? handleSceneRemove(removeScene.id) : null
        }
      />
      {addSceneGroupScenes && selectedSceneGroup && schemaData && (
        <PopupSelect
          isOpen={selectedPopup === 'ADD_SCENES'}
          title={t('popups.chooseScenesToSceneGroup')}
          selectedName={selectedSceneGroup.sceneGroup.name}
          selections={schemaData.scenes.map((scene) => ({
            id: scene.scene.id,
            name: scene.scene.name,
            defaultSelected:
              selectedSceneGroup.scenes.findIndex(
                (sceneGroupScene) => sceneGroupScene.id === scene.scene.id,
              ) !== -1,
            disabled:
              selectedSceneGroup.scenes.findIndex(
                (sceneGroupScene) => sceneGroupScene.id === scene.scene.id,
              ) !== -1,
          }))}
          isLoading={isAddingScenes}
          onClose={() =>
            setState({
              ...state,
              selectedPopup: undefined,
              addSceneGroupScenes: undefined,
              selectedSceneGroup: undefined,
            })
          }
          onConfirm={(sceneIds) => handleAddScenes(sceneIds)}
        />
      )}
      {sceneGroups && schemaData && (
        <PopupDialog
          isOpen={selectedPopup === 'CREATE_PDF'}
          title={t('popups.createPdfTitle')}
          content={<PdfSceneGroups schema={schemaData} />}
          disableMaxWidth
          isLoading={false}
          onClose={() =>
            setState({
              ...state,
              selectedPopup: undefined,
            })
          }
        />
      )}
    </>
  )
}

export default SceneGroups
